import { createContext, FC, ReactNode, useCallback, useContext } from 'react';
import { Language } from '@algoan/dashboard/model';
import { useLocalStorageState } from './use-local-storage';

type LanguageContextType =
  | {
      language: Language;
      setLanguage: (value: Language) => void;
    }
  | undefined;

const LanguageContext = createContext<LanguageContextType>(undefined);

const getInitialLanguage = (): Language => {
  if (typeof window !== 'undefined' && window.navigator?.language && window.navigator?.language === 'fr-FR')
    return Language.FR;
  else return Language.EN;
};

const useLanguage = (): { language: Language; setLanguage: (value: Language) => void } => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage should be used within a LanguageProvider');
  }
  return context;
};

const LanguageProvider: FC<{ children: ReactNode }> = ({ ...props }) => {
  const [language, setLanguageState] = useLocalStorageState<Language>('language', getInitialLanguage());

  const setLanguage = useCallback(
    (language: string) => {
      const languageWithoutCountry = language.split('_')[0] as Language;
      setLanguageState(languageWithoutCountry);
    },
    [setLanguageState],
  );

  return <LanguageContext.Provider value={{ language, setLanguage }} {...props} />;
};

export { useLanguage, LanguageProvider };
